/**
 * IndexedDB store for offline Technical Log Book drafts.
 * Phase 1: queue updates for existing server entries and sync when online.
 */

const DB_NAME = 'technical-log-book-offline';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error || new Error('Failed to open offline store'));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'entryId' });
        store.createIndex('syncStatus', 'syncStatus', { unique: false });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
  });
}

function withStore(mode, fn) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const store = tx.objectStore(STORE_NAME);
        let result;
        try {
          result = fn(store);
        } catch (err) {
          reject(err);
          return;
        }
        tx.oncomplete = () => {
          db.close();
          resolve(result);
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error || new Error('Offline store transaction failed'));
        };
        tx.onabort = () => {
          db.close();
          reject(tx.error || new Error('Offline store transaction aborted'));
        };
      })
  );
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * @param {object} params
 * @param {string} params.entryId
 * @param {object} params.payload - PUT body
 * @param {object} params.formSnapshot - full form for reopen while offline
 * @returns {Promise<object>}
 */
export async function saveOfflineDraft({ entryId, payload, formSnapshot }) {
  if (!entryId) throw new Error('entryId is required for offline draft');
  const record = {
    entryId: String(entryId),
    payload: payload || {},
    formSnapshot: formSnapshot || null,
    sheetNumber: formSnapshot?.sheetNumber || payload?.sheetNumber || '',
    aircraftRegistration:
      formSnapshot?.aircraftId?.registration ||
      formSnapshot?.aircraftRegistration ||
      '',
    updatedAt: Date.now(),
    syncStatus: 'pending',
    lastError: null
  };

  await withStore('readwrite', (store) => {
    store.put(record);
  });
  return record;
}

/**
 * @param {string} entryId
 * @returns {Promise<object|null>}
 */
export async function getOfflineDraft(entryId) {
  if (!entryId) return null;
  return withStore('readonly', (store) => requestToPromise(store.get(String(entryId))));
}

/**
 * @returns {Promise<object[]>}
 */
export async function listOfflineDrafts() {
  return withStore('readonly', (store) => requestToPromise(store.getAll())).then(
    (rows) => (Array.isArray(rows) ? rows : [])
  );
}

/**
 * @returns {Promise<string[]>}
 */
export async function listPendingOfflineEntryIds() {
  const drafts = await listOfflineDrafts();
  return drafts
    .filter((d) => d && (d.syncStatus === 'pending' || d.syncStatus === 'failed' || d.syncStatus === 'syncing'))
    .map((d) => d.entryId);
}

/**
 * @param {string} entryId
 * @returns {Promise<void>}
 */
export async function removeOfflineDraft(entryId) {
  if (!entryId) return;
  await withStore('readwrite', (store) => {
    store.delete(String(entryId));
  });
}

/**
 * @param {string} entryId
 * @param {'pending'|'syncing'|'failed'} syncStatus
 * @param {string|null} [lastError]
 * @returns {Promise<object|null>}
 */
export async function updateOfflineDraftStatus(entryId, syncStatus, lastError = null) {
  const existing = await getOfflineDraft(entryId);
  if (!existing) return null;
  const next = {
    ...existing,
    syncStatus,
    lastError: lastError || null,
    updatedAt: existing.updatedAt || Date.now()
  };
  await withStore('readwrite', (store) => {
    store.put(next);
  });
  return next;
}

/**
 * True when the error looks like a network / connectivity failure.
 * @param {unknown} error
 * @returns {boolean}
 */
export function isNetworkError(error) {
  if (!error || typeof error !== 'object') return false;
  const err = /** @type {{ code?: string, message?: string, response?: unknown, request?: unknown }} */ (error);
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return true;
  if (typeof err.message === 'string') {
    const msg = err.message.toLowerCase();
    if (msg.includes('network error') || msg.includes('failed to fetch') || msg.includes('offline')) {
      return true;
    }
  }
  // Axios: request made but no response
  if (err.request && !err.response) return true;
  return false;
}

/**
 * Merge a pending offline payload into a loaded server entry for display.
 * @param {object} serverEntry
 * @param {object|null} draft
 * @returns {object}
 */
export function mergeOfflineDraftIntoEntry(serverEntry, draft) {
  if (!serverEntry) return draft?.formSnapshot || null;
  if (!draft?.payload) return serverEntry;
  return {
    ...serverEntry,
    ...draft.payload,
    _id: serverEntry._id,
    __v: serverEntry.__v,
    createdAt: serverEntry.createdAt,
    updatedAt: serverEntry.updatedAt,
    fieldHistory: serverEntry.fieldHistory,
    statusHistory: serverEntry.statusHistory,
    techlogProof: serverEntry.techlogProof,
    workflowStatus: serverEntry.workflowStatus,
    aircraftId: serverEntry.aircraftId,
    sectorId: serverEntry.sectorId,
    openedForTripId: serverEntry.openedForTripId,
    technicalLogBookFormatId: serverEntry.technicalLogBookFormatId,
    isActive: serverEntry.isActive,
    _offlinePending: true,
    _offlineUpdatedAt: draft.updatedAt || null
  };
}
